import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Artwork } from '../types/artwork';
import { apiGet } from '../api/client';

const LIMIT = 50;

export function useArtworkList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const q = searchParams.get('q') ?? '';

  const [items, setItems] = useState<Artwork[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Increment generation — only this generation's response is accepted
    const generation = ++generationRef.current;

    const trimmedQ = q.trim();
    const path = `/api/admin/artworks?page=${page}&limit=${LIMIT}&q=${encodeURIComponent(trimmedQ)}`;

    setLoading(true);

    apiGet(path, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (generation !== generationRef.current) return; // stale response
        setItems(data.items);
        setTotal(data.total);
        setError(null);
        setLoading(false);

        // Page clamping — navigate to last valid page if current page is beyond range
        const maxPage = Math.max(1, Math.ceil(data.total / LIMIT));
        if (page > maxPage) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('page', String(maxPage));
            return next;
          });
        }
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return; // silent — not a user-facing error
        if (generation !== generationRef.current) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [page, q, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { items, total, page, limit: LIMIT, q, loading, error, refresh };
}
